import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import geminiService from '../services/geminiService';
import type { VertexAgentCourseContext } from '../services/geminiService';
import { useUserContext } from '../hooks/useUserContext';
import SendIcon from '@mui/icons-material/Send';
import AIIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { config } from '../config';

interface SessionItem {
  sessionId: string;
  displayName?: string;
  createTime?: string;
  updateTime?: string;
}

interface AISuggestion {
  id: string;
  text: string;
  icon?: React.ReactNode;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'model';
  content: string;
}

const defaultSuggestions: AISuggestion[] = [
  {
    id: '1',
    text: 'Ask me some questions to improve your understanding of my learning needs',
  },
  {
    id: '2',
    text: 'What courses would you recommend based on my role and interests?',
  },
  {
    id: '3',
    text: 'What courses will help me work toward my career goals?',
  },
];

interface AIAssistantProps {
  suggestions?: AISuggestion[];
  onSuggestionClick?: (suggestion: AISuggestion) => void;
  onSubmit?: (query: string) => void;
  variant?: 'full' | 'embedded';
  title?: string;
  subtitle?: string;
  initialMessage?: string;
  courseContext?: VertexAgentCourseContext;
}

export default function AIAssistant({
  suggestions = defaultSuggestions,
  onSuggestionClick,
  variant = 'full',
  title = 'What would you like to accomplish today?',
  subtitle = "Tell us what you're working on, and we'll help you find the right next step",
  initialMessage,
  courseContext,
}: AIAssistantProps) {
  const isEmbedded = variant === 'embedded';
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const resetVersionRef = useRef(0);
  const activeSessionIdRef = useRef<string | null>(null);
  const initialAutoSentRef = useRef(false);
  // [#3] Auto-scroll: ref attached to a sentinel div at the bottom of the message list
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { backendProfile, refreshUser } = useUserContext();

  const resolveUserId = async () => {
    const currentUserId = backendProfile?.id?.trim();
    if (currentUserId) {
      return currentUserId;
    }

    const refreshedProfile = await refreshUser();
    const refreshedUserId = refreshedProfile?.id?.trim();

    return refreshedUserId || null;
  };

  const toIsoDateTime = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }

    if (typeof value === 'number' && value > 0) {
      return new Date(value * 1000).toISOString();
    }

    return undefined;
  };

  const loadHistory = async () => {
    if (historyLoaded) return;
    setHistoryLoading(true);
    try {
      const resolvedUserId = await resolveUserId();
      if (!resolvedUserId) {
        setHistoryLoading(false);
        return;
      }
      const data = await geminiService.listSessions();
      // Response may be an array or nested under output.sessions
      const raw: unknown[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.output?.sessions)
          ? data.output.sessions
          : (data?.sessions ?? []);
      const mapped: SessionItem[] = raw.map((s: unknown) => {
        const session = s as Record<string, unknown>;
        // Extract sessionId from full resource name like "projects/.../sessions/{id}"
        const name = (session.name as string) ?? '';
        const parts = name.split('/');
        const id = parts[parts.length - 1] || (session.id as string) || (session.sessionId as string) || name;
        return {
          sessionId: id,
          displayName: (session.displayName as string) || undefined,
          createTime: toIsoDateTime(session.createTime),
          updateTime: toIsoDateTime(session.updateTime ?? session.lastUpdateTime),
        };
      });
      mapped.sort(
        (a, b) =>
          new Date(b.updateTime ?? b.createTime ?? 0).getTime() -
          new Date(a.updateTime ?? a.createTime ?? 0).getTime()
      );
      setSessions(mapped);
      setHistoryLoaded(true);
    } catch {
      // silently fail — history is non-critical
    }
    setHistoryLoading(false);
  };

  // Auto-load history when user profile becomes available
  useEffect(() => {
    if (backendProfile?.id) {
      void loadHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendProfile?.id]);

  // Auto-send initial message if provided (e.g., navigated from another page)
  useEffect(() => {
    if (initialMessage && !initialAutoSentRef.current) {
      initialAutoSentRef.current = true;
      void sendMessage(initialMessage);
    }
  // Only run once on mount — sendMessage is stable enough for this one-shot use
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // [#3] Auto-scroll to bottom whenever messages change or loading state changes
  useEffect(() => {
    if (messages.length > 0 || loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const filteredSessions = sessionSearch
    ? sessions.filter((s) =>
        (s.displayName || '').toLowerCase().includes(sessionSearch.toLowerCase())
      )
    : sessions;

  const extractTextFromContent = (value: unknown): string => {
    if (!value) return '';

    if (typeof value === 'string') {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value
        .map(extractTextFromContent)
        .filter(Boolean)
        .join('\n')
        .trim();
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;

      if (typeof record.text === 'string') {
        return record.text.trim();
      }

      if (Array.isArray(record.parts)) {
        return extractTextFromContent(record.parts);
      }

      if (record.content) {
        return extractTextFromContent(record.content);
      }
    }

    return '';
  };

  const normalizeHeadingLevelsToH3 = (html: string): string =>
    html
      .replace(/<h[1-6](\s[^>]*)?>/gi, '<h3$1>')
      .replace(/<\/h[1-6]>/gi, '</h3>');

  const normalizeAssistantLinks = (html: string): string =>
    html.replace(/<a\s+([^>]*href\s*=\s*["'][^"']+["'][^>]*)>/gi, (_match, attrs) => {
      const hasTarget = /\btarget\s*=\s*/i.test(attrs);
      const hasRel = /\brel\s*=\s*/i.test(attrs);

      let normalized = `<a ${attrs}`;
      if (!hasTarget) {
        normalized += ' target="_blank"';
      }
      if (!hasRel) {
        normalized += ' rel="noopener noreferrer"';
      }
      normalized += '>';

      return normalized;
    });

  const mapSessionEventsToMessages = (events: unknown[]): ChatMessage[] =>
    events.flatMap((event) => {
      if (!event || typeof event !== 'object') {
        return [];
      }

      const eventRecord = event as Record<string, unknown>;
      const contentRecord = (eventRecord.content as Record<string, unknown> | undefined) ?? undefined;
      const rawRole =
        typeof contentRecord?.role === 'string'
          ? contentRecord.role
          : typeof eventRecord.role === 'string'
            ? eventRecord.role
            : typeof eventRecord.author === 'string'
              ? eventRecord.author
              : '';
      const roleValue = rawRole.trim().toLowerCase();

      if (roleValue && !['user', 'model', 'assistant'].includes(roleValue)) {
        return [];
      }

      const text = extractTextFromContent(contentRecord?.parts ?? contentRecord ?? eventRecord.content ?? eventRecord);
      if (!text) {
        return [];
      }

      return [
        {
          role: roleValue === 'user' ? 'user' : roleValue === 'model' ? 'model' : 'assistant',
          content: text,
        },
      ];
    });

  const loadSessionMessages = async (id: string) => {
    resetVersionRef.current += 1;
    const requestVersion = resetVersionRef.current;

    activeSessionIdRef.current = id;
    setSessionId(id);
    setMessages([]);
    setQuery('');
    setHasStartedConversation(true);
    setLoading(true);

    try {
      const resolvedUserId = await resolveUserId();
      if (requestVersion !== resetVersionRef.current) {
        return;
      }

      if (!resolvedUserId) {
        setMessages([
          {
            role: 'assistant',
            content: 'Your profile is still loading. Please wait a moment and try again.',
          },
        ]);
        return;
      }

      const response = await geminiService.getSession({
        sessionId: id,
      });

      if (requestVersion !== resetVersionRef.current) {
        return;
      }

      const rawEvents = Array.isArray(response)
        ? response
        : Array.isArray(response?.output?.events)
          ? response.output.events
          : Array.isArray(response?.events)
            ? response.events
            : [];

      const restoredMessages = mapSessionEventsToMessages(rawEvents);
      setMessages(
        restoredMessages.length > 0
          ? restoredMessages
          : [{ role: 'assistant', content: 'This chat has no retrievable messages yet.' }]
      );
      setHasStartedConversation(true);
    } catch {
      if (requestVersion !== resetVersionRef.current) {
        return;
      }

      setMessages([
        {
          role: 'assistant',
          content: 'Sorry, there was an error loading this chat.',
        },
      ]);
      setHasStartedConversation(true);
    } finally {
      if (requestVersion === resetVersionRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSelectSession = (id: string) => {
    activeSessionIdRef.current = id;
    setSessionId(id);
    void loadSessionMessages(id);
  };

  const handleNewChat = () => {
    resetVersionRef.current += 1;
    activeSessionIdRef.current = null;
    setSessionId(null);
    setMessages([]);
    setHasStartedConversation(false);
    setQuery('');
    setLoading(false);
    setIsCreatingSession(false);
    setSessionSearch('');
  };

  const formatSessionDate = (iso?: string, includeTime = false) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
      });
    } catch {
      return null;
    }
  };

  const sendMessage = async (inputText?: string) => {
    const text = (inputText !== undefined ? inputText : query).trim();
    if (!text || loading || isCreatingSession) return;

    const requestVersion = resetVersionRef.current;

    setHasStartedConversation(true);
    setLoading(true);

    const resolvedUserId = await resolveUserId();
    if (requestVersion !== resetVersionRef.current) {
      return;
    }

    if (!resolvedUserId) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Your profile is still loading. Please wait a moment and try again.',
        },
      ]);
      setLoading(false);
      return;
    }

    let currentSessionId = activeSessionIdRef.current ?? sessionId;

    if (currentSessionId && sessionId !== currentSessionId) {
      setSessionId(currentSessionId);
    }

    // Create session on first message if it doesn't exist
    if (!currentSessionId && !isCreatingSession) {
      setIsCreatingSession(true);
      try {
        const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || config.timeZone.default;
        const response = await geminiService.createSession({
          userId: resolvedUserId,
          displayName: text.substring(0, 100),
          timeZone: browserTimeZone,
        });
        if (requestVersion !== resetVersionRef.current) {
          return;
        }
        currentSessionId = response?.output?.sessionId || response?.sessionId || null;
        activeSessionIdRef.current = currentSessionId;
        setSessionId(currentSessionId);
        // Optimistically add the new session to the sidebar list
        if (currentSessionId) {
          setSessions((prev) => [
            { sessionId: currentSessionId!, displayName: text.substring(0, 100), createTime: new Date().toISOString() },
            ...prev,
          ]);
        }
      } catch (err) {
        if (requestVersion !== resetVersionRef.current) {
          return;
        }
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Failed to initialize session. Please try again.' },
        ]);
        setIsCreatingSession(false);
        setLoading(false);
        return;
      }
      setIsCreatingSession(false);
    }

    const userMessage: ChatMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setQuery('');

    try {
      const response = await geminiService.streamQuery({
        sessionId: currentSessionId!,
        message: userMessage.content,
        courseContext,
      });

      if (requestVersion !== resetVersionRef.current) {
        return;
      }

      // Parse response robustly across array/object payloads and multiple events.
      const rawEvents: unknown[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.output?.events)
          ? response.output.events
          : Array.isArray(response?.events)
            ? response.events
            : [response];

      const aiMessages = mapSessionEventsToMessages(rawEvents).filter((m) => m.role !== 'user');
      const aiText = aiMessages
        .map((m) => m.content.trim())
        .filter(Boolean)
        .join('\n\n') || 'Sorry, no response.';

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: aiText },
      ]);
    } catch (err) {
      if (requestVersion !== resetVersionRef.current) {
        return;
      }
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, there was an error.' },
      ]);
    }
    if (requestVersion === resetVersionRef.current) {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    sendMessage();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestionClick = (suggestion: AISuggestion) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    } else {
      sendMessage(suggestion.text);
    }
  };

  return (
    /* Outer container: row layout — left sidebar + right chat area */
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        // Embedded variant: bounded height (used inside scrollable course-detail pages).
        // Standalone: fill the flex parent (MainLayout hides the footer on this route).
        height: isEmbedded
          ? (hasStartedConversation ? 'min(70vh, 680px)' : 'auto')
          : '100%',
        minHeight: isEmbedded
          ? (hasStartedConversation ? 520 : undefined)
          : 640,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {isEmbedded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'tint.primarySoft',
          }}
        >
          <AIIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              color: 'text.primary',
            }}
          >
            AI Assistant
          </Typography>
        </Box>
      )}

      {/* ── Left Sidebar — hidden on mobile ──
          Three visual tiers, each with one job:
            1. "New chat" — the only primary action (filled contained button).
            2. Search field — always visible, ambient input. No mode toggle.
            3. Sessions list — the only place "selected" tint appears.
          Previous design conflated mode toggles (Search/Recents) with the
          active-session highlight, making it unclear which row was "you are here". */}
      {!isEmbedded && (
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Tier 1 — Primary action */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
            onClick={handleNewChat}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: '13.5px',
              fontWeight: 600,
              borderRadius: 1.5,
              py: 0.875,
              px: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none', bgcolor: 'primary.dark' },
            }}
          >
            New chat
          </Button>
        </Box>

        {/* Tier 2 — Ambient search input */}
        <Box sx={{ px: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search chats…"
            value={sessionSearch}
            onChange={(e) => setSessionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '13px',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                pl: 1,
                '& .MuiOutlinedInput-input': { py: 0.625, pl: 0 },
              },
            }}
          />
        </Box>

        {/* Recents header — non-interactive label, low typographic weight */}
        <Box
          sx={{
            px: 2.25,
            pt: 2,
            pb: 0.75,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
            Recents
          </Typography>
          {sessions.length > 0 && (
            <Typography
              component="span"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 500,
                color: 'text.disabled',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
              }}
            >
              {sessions.length}
            </Typography>
          )}
        </Box>

        {/* Tier 3 — Sessions list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
          }}
        >
          {historyLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {!historyLoading && filteredSessions.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2.25, py: 1, fontSize: '12.5px', fontStyle: 'italic' }}
            >
              {sessionSearch ? 'No matching chats.' : 'No previous chats.'}
            </Typography>
          )}
          {!historyLoading && filteredSessions.length > 0 && (
            <List dense disablePadding sx={{ px: 0.5, py: 0.25 }}>
              {filteredSessions.map((s) => {
                const sessionLabel =
                  s.displayName ||
                  formatSessionDate(s.updateTime, true) ||
                  formatSessionDate(s.createTime, true) ||
                  `Session ${s.sessionId.slice(-8)}`;

                return (
                  <ListItemButton
                    key={s.sessionId}
                    selected={s.sessionId === sessionId}
                    onClick={() => handleSelectSession(s.sessionId)}
                    sx={{
                      mx: 0.5,
                      mb: 0.375,
                      px: 1.25,
                      py: 0.625,
                      minHeight: 34,
                      borderRadius: 1,
                      '& .MuiListItemText-root': { my: 0 },
                      // [#7] Softer selected state — tinted bg + primary text instead of solid blue fill
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        '&:hover': { bgcolor: 'tint.primaryStrong' },
                        '& .MuiTypography-root': { color: 'primary.main', fontWeight: 600 },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '13px',
                            fontWeight: 400,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {sessionLabel}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
      )}

      {/* ── Right: main chat area ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          pt: isEmbedded ? 5.5 : 0,
        }}
      >
        {/* Chat messages — expands to fill space once conversation starts */}
        <Box
          sx={{
            flex: hasStartedConversation ? 1 : '0 0 auto',
            minHeight: 0,
            maxHeight: hasStartedConversation ? 'none' : 240,
            overflowY: 'auto',
            mb: hasStartedConversation ? 0 : 2,
            px: 3,
            pt: 2,
            pb: hasStartedConversation ? 1 : 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {messages.map((msg, idx) => (
            // [#2] Chat bubbles, [#4] No prefix labels, [#6] mb: 2 spacing
            <Box
              key={idx}
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              {msg.role === 'user' ? (
                // [#2] User bubble: right-aligned blue filled pill
                <Box
                  sx={{
                    maxWidth: '75%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    px: 2,
                    py: 1,
                    borderRadius: '18px 18px 4px 18px',
                    fontSize: '15px',
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                    boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.primary.main, 0.25)}`,
                  }}
                >
                  {msg.content}
                </Box>
              ) : (
                // [#2] AI bubble: left-aligned gray card with markdown
                <Box
                  sx={{
                    maxWidth: '80%',
                    bgcolor: 'tint.neutralSoft',
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 2,
                    py: 1,
                    borderRadius: '4px 18px 18px 18px',
                    fontSize: '15px',
                    lineHeight: 1.6,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                    '& p': { m: 0, mb: 0.75 },
                    '& p:last-child': { mb: 0 },
                    '& ul, & ol': { mt: 0.5, mb: 0.5, pl: 2.5 },
                    '& li': { mb: 0.25 },
                    '& strong': { fontWeight: 600 },
                    '& code': { fontSize: '13px', bgcolor: 'tint.neutralMuted', px: 0.5, borderRadius: '3px' },
                    '& a': {
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                    },
                    '& a:hover': {
                      color: 'primary.dark',
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      normalizeAssistantLinks(
                        normalizeHeadingLevelsToH3(marked.parse(msg.content || '') as string)
                      )
                    ),
                  }}
                />
              )}
            </Box>
          ))}
          {/* [#5] Animated typing indicator — replaces bare CircularProgress */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  px: 2,
                  py: 1.25,
                  bgcolor: 'tint.neutralSoft',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '4px 18px 18px 18px',
                }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: 'text.disabled',
                      animation: 'aiTypingBounce 1.2s ease-in-out infinite',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes aiTypingBounce': {
                        '0%, 60%, 100%': { transform: 'translateY(0)' },
                        '30%': { transform: 'translateY(-6px)', bgcolor: 'primary.main' },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          {/* [#3] Scroll sentinel */}
          <div ref={messagesEndRef} />
        </Box>

        {/* AI Prompt Area: only show if no user messages yet */}
        {!hasStartedConversation && (
          <Box
            sx={{
              flex: isEmbedded ? '0 0 auto' : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: isEmbedded ? 'flex-start' : 'center',
              p: isEmbedded ? 2 : 3,
              overflowY: 'auto',
            }}
          >
            {/* Main Heading — inline sparkle keeps brand signal without the cliché circle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <AIIcon sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: 1.25,
                  letterSpacing: '-0.01em',
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* Subheading */}
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{
                mb: 4,
                fontSize: '15px',
                lineHeight: 1.6,
                maxWidth: '52ch',
              }}
            >
              {subtitle}
            </Typography>

          {/* AI Suggestions */}
          <Stack spacing={0.75} sx={{ width: '100%', maxWidth: 440 }}>
            {suggestions.map((suggestion) => (
              <Box
                key={suggestion.id}
                role="button"
                tabIndex={0}
                onClick={() => handleSuggestionClick(suggestion)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSuggestionClick(suggestion);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'tint.primaryStrong',
                  bgcolor: 'background.paper',
                  outline: 'none',
                  transition: 'all 0.16s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover, &:focus-visible': {
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderColor: 'primary.main',
                    bgcolor: 'tint.primarySoft',
                    '& .suggestion-arrow': {
                      opacity: 1,
                      transform: 'translateX(3px)',
                    },
                  },
                }}
              >
                {suggestion.icon && (
                  <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0, fontSize: 17 }}>
                    {suggestion.icon}
                  </Box>
                )}
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '13.5px',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    color: 'text.primary',
                  }}
                >
                  {suggestion.text}
                </Typography>
                <ArrowForwardIcon
                  className="suggestion-arrow"
                  sx={{
                    fontSize: 14,
                    color: 'primary.main',
                    flexShrink: 0,
                    opacity: 0.28,
                    transition: 'opacity 0.16s ease, transform 0.16s ease',
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

        {/* Input Area — sits on the same surface as the chat. The TextField's own
            outline plus the single top divider provide all the visual separation needed.
            Previously had a gray bg that stacked awkwardly against the footer's borders. */}
        <Box
          sx={{
            p: isEmbedded ? 1.5 : 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            mt: isEmbedded ? 0 : 'auto',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder={isEmbedded ? 'Ask about this course…' : 'Ask anything'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={loading || isCreatingSession}
            sx={{
              mb: isEmbedded ? 0 : 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)', // generic neutral drop shadow
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'text.disabled' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
            }}
            // [#7] Send button: contained pill instead of bare IconButton
            InputProps={{
              endAdornment: (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!query.trim() || loading || isCreatingSession}
                  sx={{
                    ml: 1,
                    minWidth: 0,
                    borderRadius: '100px',
                    px: 1.5,
                    py: 0.75,
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </Button>
              ),
            }}
          />

        </Box>
      </Box>
    </Box>
  );
}
